import { onUnmounted, ref } from "vue";
import * as signalR from "@microsoft/signalr";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import type { RunHistory, ServiceHistory, ServiceRunStatus } from "@/types";

/** The address and token the service issues us. Ours is a short-lived, per-user ticket. */
interface ClientTicket {
  url: string;
  accessToken: string;
}

/**
 * A run's state as the engine sends it. Travels over the hub rather than an HTTP response, so this type
 * is hand-written rather than generated — it must match `RunProgressUpdate` in the API.
 */
export interface RunProgressUpdate {
  runId: string;
  workflowId: string;
  status: string;
  services: {
    serviceId: string;
    serviceName: string;
    status: string;
    started: boolean;
    total: number;
    succeeded: number;
    warning: number;
    failed: number;
    neverResponded: number;
    /** The service's own time on the job. Zero while it is still going — not known yet, not nothing. */
    processingMs: number;
  }[];
  errors: string[];
}

/**
 * What one service's transactions have come to while it is still running. Sent once per cursor
 * checkpoint by the service that moved — between a service starting and finishing, the run itself does
 * not mutate, so this is the only thing that arrives. Must match `ServiceCountsUpdate` in the API.
 */
export interface ServiceCountsUpdate {
  runId: string;
  serviceId: string;
  total: number;
  succeeded: number;
  warning: number;
  failed: number;
  neverResponded: number;
  /** What `total` is climbing towards, projected from the parents the service has read so far. */
  expected: number;
}

interface RunFinished {
  runId: string;
  workflowId: string;
}

/**
 * Live progress for the runs on screen. Azure SignalR runs in Serverless mode: we ask the API
 * for a ticket, connect to the service with it, and tell the API which run we want — the API is where
 * the org check lives, so a run we may not read is one we are never put on.
 *
 * <p>Frames are lossy by design and `onFinished` is the guaranteed last word, which is why the caller
 * refetches the run there rather than trusting the final frame's counts.</p>
 */
export function useRunProgress(options: {
  /** Applies a frame. Receives the previous run so structure the frame omits is carried forward. */
  onProgress: (update: RunProgressUpdate) => void;
  /** Applies one service's live counts — what arrives while a service is working. */
  onServiceCounts: (update: ServiceCountsUpdate) => void;
  onFinished: (finished: RunFinished) => void;
}) {
  const connected = ref(false);
  // Set when we can't establish/keep the live connection, so a consumer can tell the user the canvas
  // has stopped updating rather than leaving a live run looking frozen-but-done. Cleared on connect.
  const connectionError = ref<string | null>(null);
  let connection: signalR.HubConnection | null = null;
  let connecting: Promise<signalR.HubConnection> | null = null;
  const watching = new Set<string>();

  const negotiateUrl = `${import.meta.env.VITE_API_URL}/realtime/runs/negotiate`;

  // Every step of getting on a run, in order, so a screen that never updates can be read back to which
  // step it stopped at. Development only — a watched run produces a frame per mutation.
  const trace = (step: string, ...detail: unknown[]) => {
    if (import.meta.env.DEV) console.debug(`[run-progress] ${step}`, ...detail);
  };

  // Called again on every reconnect, so an expired token is replaced rather than retried.
  const ticket = () => fetchWrapper.post<ClientTicket>(negotiateUrl);

  /**
   * The in-flight promise is the guard, not the connection: the connection is only assigned after a
   * negotiate round trip and a handshake, so two callers arriving in that window would each build one
   * and the loser's handlers would sit on an orphan that still receives frames.
   */
  function connect() {
    connecting ??= start().catch((error) => {
      connecting = null; // A cached rejection would make every later attempt fail without trying.
      connectionError.value = error?.message ?? "Live updates are unavailable.";
      throw error;
    });
    return connecting;
  }

  async function start() {
    const first = await ticket();
    trace("ticket issued", first.url);

    const started = new signalR.HubConnectionBuilder()
      .withUrl(first.url, { accessTokenFactory: async () => (await ticket()).accessToken })
      .withAutomaticReconnect()
      .configureLogging(import.meta.env.DEV ? signalR.LogLevel.Debug : signalR.LogLevel.Warning)
      .build();

    started.on("RunProgress", (update: RunProgressUpdate) => {
      trace("frame", update.runId, update.status);
      options.onProgress(update);
    });
    started.on("ServiceCounts", (update: ServiceCountsUpdate) => {
      trace("counts", update.serviceId, update.total);
      options.onServiceCounts(update);
    });
    started.on("RunCompleted", (finished: RunFinished) => {
      trace("completed", finished.runId);
      options.onFinished(finished);
    });
    started.onreconnecting(() => {
      connected.value = false;
      trace("reconnecting");
    });
    started.onreconnected(() => {
      connected.value = true;
      connectionError.value = null; // back on the wire
      // Group membership is keyed on the user, so it survives the drop — but a reconnect after a long
      // gap may have missed the completion, and re-watching is cheap next to a screen stuck on Running.
      trace("reconnected", [...watching].join(", ") || "(watching nothing)");
      for (const runId of watching) void tell("watch", runId);
    });
    started.onclose((error) => {
      connected.value = false;
      connection = null;
      connecting = null;
      // A clean stop (unmount) closes with no error; only a real drop that automatic-reconnect
      // couldn't recover surfaces one — that's the case worth telling the user about.
      if (error) connectionError.value = error.message ?? "Live updates disconnected.";
      trace("closed", error?.message ?? "(no error)");
    });

    await started.start();
    connection = started;
    connected.value = true;
    connectionError.value = null;
    trace("connected");
    return started;
  }

  /**
   * Follow exactly these runs — joins the ones we are not on, leaves the ones no longer named. A
   * screen showing a page of runs has as many going at once as the user started, and each is its own
   * group on the service, so following one at a time is what leaves the others stale until a refresh.
   */
  async function watch(runIds: readonly string[]) {
    const wanted = new Set(runIds);
    const joining = runIds.filter((runId) => !watching.has(runId));

    for (const runId of watching) {
      if (wanted.has(runId)) continue;
      watching.delete(runId);
      void tell("unwatch", runId);
    }

    if (joining.length === 0) return;
    await connect();
    for (const runId of joining) {
      watching.add(runId);
      void tell("watch", runId);
    }
    trace("watching", [...watching].join(", "));
  }

  // A failure here costs live updates, not the run — the caller's own refetch still shows the outcome.
  async function tell(what: "watch" | "unwatch", runId: string) {
    const url = `${import.meta.env.VITE_API_URL}/realtime/runs/${runId}/watch`;
    try {
      await (what === "watch" ? fetchWrapper.post(url) : fetchWrapper.delete(url));
    } catch {
      /* ignored */
    }
  }

  onUnmounted(() => {
    watching.clear();
    void connection?.stop();
    connection = null;
  });

  return { watch, connected, connectionError };
}

/**
 * A frame carries status and counts, not shape: the engine's snapshot names no service type and no
 * parents. Those come off the run already on screen — the optimistic placeholder built from the
 * workflow, or the run as it was fetched — so a frame updates a canvas rather than rebuilding one.
 */
export function applyProgress(previous: RunHistory | null, update: RunProgressUpdate): RunHistory | null {
  if (!previous) return null;

  const known = new Map(previous.services.map((service) => [service.serviceId, service]));
  const services: ServiceHistory[] = update.services.map((service) => ({
    serviceId: service.serviceId,
    serviceName: service.serviceName,
    serviceType: known.get(service.serviceId)?.serviceType ?? "",
    status: asServiceRunStatus(service.status),
    total: service.total,
    succeeded: service.succeeded,
    warning: service.warning,
    failed: service.failed,
    neverResponded: service.neverResponded,
    // A run frame is built from the run's own document, which records nothing about a service still
    // creating — so what it is heading for stands from the counts frame that last said.
    expected: known.get(service.serviceId)?.expected ?? 0,
    // A service still going has no duration yet and sends zero, so the last known value stands — which
    // is what keeps a finished service's time on screen through every later frame the run produces.
    processingMs: service.processingMs || known.get(service.serviceId)?.processingMs || 0,
    achievedConcurrency: known.get(service.serviceId)?.achievedConcurrency ?? null,
    parentServiceIds: known.get(service.serviceId)?.parentServiceIds ?? [],
  }));

  return {
    ...previous,
    id: update.runId,
    workflowId: update.workflowId,
    status: asServiceRunStatus(update.status),
    services,
    errors: update.errors.map((message) => ({ message })),
  };
}

/**
 * Applies one service's live counts to the run on screen. Counts only — a service's status stays what
 * the run says, because a service with nothing failed yet is not a service that succeeded.
 */
export function applyServiceCounts(
  previous: RunHistory | null,
  update: ServiceCountsUpdate
): RunHistory | null {
  if (!previous) return null;

  const index = previous.services.findIndex((service) => service.serviceId === update.serviceId);
  if (index < 0) return previous;

  const services = [...previous.services];
  services[index] = {
    ...services[index],
    total: update.total,
    succeeded: update.succeeded,
    warning: update.warning,
    failed: update.failed,
    neverResponded: update.neverResponded,
    expected: update.expected,
  };

  return { ...previous, services };
}

// The engine's Status enum is wider than what the canvas draws. Pending and Queued both mean still to
// come, which is exactly how the canvas reads Pending — so anything it doesn't colour lands there.
function asServiceRunStatus(status: string): ServiceRunStatus {
  switch (status) {
    case "Succeeded":
    case "Failed":
    case "Warning":
      return status;
    default:
      return "Pending";
  }
}
